import type { TFunction } from 'i18next';
import type {
  CreatePromotionRequest,
  EngagementInsights,
  EngagementSettings,
  EngagementTemplate,
  ExperimentVariantRow,
  FunnelRow,
  Metric,
  RoasRow,
} from 'types/engagement.types';
import { fmtPct, isMetric } from '../shared/format';

/** Pure rules of the Engagement Center tabs (kept out of the component files for tests and fast refresh). */

export type PromotionDraft = Omit<CreatePromotionRequest, 'dryRun'>;

const FUNNEL_ORDER = ['targeted', 'sent', 'delivered', 'opened', 'clicked', 'returned', 'completed'];

const asMetric = (v: number | Metric): Metric => (isMetric(v) ? (v as Metric) : { value: v as number, status: 'ok' });

/** Tab slugs are part of the contract (Oberfläche) and of the URL: /administration/engagement/<slug>. */
export const ENGAGEMENT_TABS = [
  'kunden',
  'nachrichten',
  'vorlagen',
  'automationen',
  'aktionen',
  'wachstum',
  'umsatz',
  'bindung',
  'wirkung',
  'einstellungen',
] as const;

export type EngagementTabSlug = (typeof ENGAGEMENT_TABS)[number];

export const resolveEngagementTab = (slug: string | undefined): EngagementTabSlug =>
  (ENGAGEMENT_TABS as readonly string[]).includes(slug ?? '') ? (slug as EngagementTabSlug) : 'kunden';

/** Cost per customer over all paid rows — only if every row's spend is known. */
export const totalCostPerCustomer = (rows: FunnelRow[]): Metric => {
  const paid = rows.filter((r) => !(r.spend?.status === 'ok' && r.spend.value === 0));
  const missing = paid.find((r) => r.spend?.status !== 'ok');
  if (missing) return { value: null, status: missing.spend?.status ?? 'pending', reason: missing.spend?.reason };
  const spend = paid.reduce((a, r) => a + (r.spend.value ?? 0), 0);
  const purchases = paid.reduce((a, r) => a + r.purchases, 0);
  if (purchases === 0) return { value: null, status: 'pending', reason: undefined };
  return { value: spend / purchases, status: 'ok' };
};

export const experimentDecisionText = (row: ExperimentVariantRow, t: TFunction, lng: string): string => {
  if (row.decision === 'winner') {
    return t('engExpWinner', {
      variant: row.variantLabel ?? row.variant,
      probability: row.probabilityBest !== null && row.probabilityBest !== undefined ? fmtPct(lng)(row.probabilityBest) : '—',
    });
  }
  if (row.decision === 'loser') return t('engExpLoser');
  const share = row.requiredVisitors > 0 ? Math.min(1, row.views / row.requiredVisitors) : 0;
  return `${t('engExpUndecided')} · ${t('engExpProgress', { day: row.daysRunning, share: fmtPct(lng)(share) })}`;
};

/** The API sends the funnel as `{ step: n }`; output keeps the contract order. */
export const normalizeFunnel = (funnel: EngagementInsights['funnel'] | undefined): { step: string; value: number }[] => {
  if (!funnel) return [];
  const entries = Object.entries(funnel);
  const map = new Map(entries.map(([k, v]) => [k, typeof v === 'number' ? v : 0]));
  const ordered = FUNNEL_ORDER.filter((k) => map.has(k)).map((k) => ({ step: k, value: map.get(k) as number }));
  const rest = Array.from(map.entries())
    .filter(([k]) => !FUNNEL_ORDER.includes(k))
    .map(([step, value]) => ({ step, value }));
  return [...ordered, ...rest];
};

/** Sum of a column — only when every row has a value; otherwise the first missing row's status. */
export const sumMetric = (rows: RoasRow[], key: 'spendCents' | 'revenueCents'): Metric => {
  let total = 0;
  for (const r of rows) {
    const m = asMetric(r[key]);
    if (m.status !== 'ok' || m.value === null) return { value: null, status: m.status === 'ok' ? 'pending' : m.status, reason: m.reason };
    total += m.value;
  }
  return { value: total, status: 'ok' };
};

export const ratioMetric = (num: Metric, den: Metric): Metric => {
  if (num.status !== 'ok' || num.value === null) return num;
  if (den.status !== 'ok' || den.value === null) return den;
  if (den.value === 0) return { value: null, status: 'pending', reason: undefined };
  return { value: num.value / den.value, status: 'ok' };
};

/** Every field must be a finite, non-negative number; minutes may have decimals. */
export const settingsValid = (s: EngagementSettings): boolean =>
  [...Object.values(s.timeSaved), ...Object.values(s.caps), s.sunsetAfterUnopened].every((n) => typeof n === 'number' && Number.isFinite(n) && n >= 0);

/**
 * Whether the current wording was tested. The API says so (`testedCurrentWording`: the last test
 * covered exactly this text); without that field only "ever tested" is known.
 */
export const templateTested = (tpl: EngagementTemplate): boolean =>
  typeof tpl.testedCurrentWording === 'boolean' ? tpl.testedCurrentWording : !!tpl.lastTestedAt;

/** Status column: a template whose current wording was never tested cannot be trusted, whatever `active` says. */
export const templateStatus = (tpl: EngagementTemplate): 'active' | 'inactive' | 'untested' =>
  !templateTested(tpl) ? 'untested' : tpl.active ? 'active' : 'inactive';

/** Template is savable only with a name, a key and the mandatory recipient benefit. */
export const templateValidation = (tpl: EngagementTemplate): string[] => {
  const missing: string[] = [];
  if (!tpl.key.trim()) missing.push('key');
  if (!tpl.name.trim()) missing.push('name');
  if (!tpl.recipientBenefit.trim()) missing.push('recipientBenefit');
  return missing;
};

/** The API's limits for POST /promotions (lifecycle/admin.routes.ts CreatePromotionSchema). */
export const PROMOTION_LIMITS = {
  nameMin: 3,
  percentOffMax: 60,
  durationMonthsMax: 12,
  endsAfterDaysMax: 60,
  maxRedemptionsMax: 1000,
  controlGroupPercentMax: 50,
} as const;

const intIn = (n: number, min: number, max: number) => Number.isInteger(n) && n >= min && n <= max;

/** A promotion needs an end and a cap (contract: "Rabatte mit Deckel"), within the API's limits. */
export const promotionDraftValid = (d: PromotionDraft): boolean =>
  d.name.trim().length >= PROMOTION_LIMITS.nameMin &&
  intIn(d.discount.percentOff, 1, PROMOTION_LIMITS.percentOffMax) &&
  intIn(d.discount.durationMonths, 1, PROMOTION_LIMITS.durationMonthsMax) &&
  intIn(d.endsAfterDays, 1, PROMOTION_LIMITS.endsAfterDaysMax) &&
  intIn(d.maxRedemptions, 1, PROMOTION_LIMITS.maxRedemptionsMax) &&
  intIn(d.controlGroupPercent, 0, PROMOTION_LIMITS.controlGroupPercentMax);
